import type { LiftSummary } from '~/calculations/estimated1RM'
import type { Projection } from '~/calculations/projection'
import type { WeightTrend } from '~/calculations/weightTrends'
import type { AthleteProfileRecord } from '~/models/athleteProfile'
import type { GoalRecord } from '~/models/goal'
import type { MeasurementSessionRecord } from '~/models/measurementSession'
import type { PhaseRecord } from '~/models/phase'
import type { PhotoRecord } from '~/models/photo'
import type { ProjectRecord } from '~/models/project'
import type { OpinionRecord, RulesOutcome } from '~/models/rulesOpinion'
import type { SentinelLiftRecord } from '~/models/sentinelLift'
import type { StrengthEntryRecord } from '~/models/strengthEntry'
import type { TrainingWeekRecord } from '~/models/trainingWeek'
import type { WeeklyReviewRecord } from '~/models/weeklyReview'
import type { WeightEntryRecord } from '~/models/weightEntry'
import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import type { PrivacySelection, ReportingPeriod } from './scope'

/**
 * The single intermediate representation every export renderer reads
 * (implementation-plan.md §5).
 *
 * JSON is the only renderer at Checkpoint 1.7; PDF and CSV arrive at Checkpoint
 * 4 and will read this same model. That is what makes "PDF and JSON agree
 * semantically" structurally true rather than a thing to remember: a renderer
 * may omit detail for readability, but it cannot contradict the model, because
 * it has no other source.
 *
 * Redaction happens when this model is BUILT, before any renderer sees it
 * (implementation-plan.md §8, "Export leaks excluded content"). A renderer
 * therefore cannot leak an excluded value, because it never receives one.
 */
export interface ReportModel {
  readonly generatedAt: IsoTimestamp
  readonly reference: CalendarDate
  readonly period: ReportingPeriod
  readonly selection: PrivacySelection
  readonly profile: AthleteProfileRecord
  /**
   * The project the week belongs to (D-062).
   *
   * A small block inside the existing default scope — name, phase type, week N
   * of M, target date — not the project's whole life. The full completion
   * summary spans months and is its own user-initiated export, so D-019's
   * current-week-plus-four default stays untouched and a project-lifetime
   * disclosure is never the default.
   */
  readonly project: ProjectRecord | undefined
  readonly phase: PhaseRecord | undefined
  readonly goal: GoalRecord | undefined
  /** Already filtered to the reporting period. */
  readonly weightEntries: readonly WeightEntryRecord[]
  readonly weightTrend: WeightTrend
  /** Already filtered to the reporting period, newest last. */
  readonly measurementSessions: readonly MeasurementSessionRecord[]
  readonly outcome: RulesOutcome
  /**
   * Finalized reviews in the period, oldest first, each with the opinion it was
   * actually given (D-046).
   *
   * **These are the answers the user received, not answers recomputed now.**
   * Every one carries its own immutable `phaseSnapshot`, so an export shows what
   * was said at the time even if the phase has since been corrected (D-070).
   */
  readonly weeklyReviews: readonly ReviewWithOpinion[]
  /** Active sentinel lifts with their derived estimates. Never stored derived. */
  readonly liftSummaries: readonly ExportLiftSummary[]
  /** Raw sets in the period, oldest first — the evidence behind the estimates. */
  readonly strengthEntries: readonly StrengthEntryRecord[]
  readonly projection: Projection
  /**
   * Photos captured in the reporting period, oldest first (D-082).
   *
   * **Metadata only — no bytes.** The image itself belongs in the optional media
   * ZIP or, as a compressed thumbnail, in the PDF; `privacy-and-storage.md`
   * forbids photo bytes inline in JSON. Carrying the records here regardless is
   * what lets the export say *which* photos exist and would be included, and
   * lets the manifest link a file back to the week it documents.
   *
   * The list is populated whatever the selection says. **Gating happens at
   * serialization**, as it does for every other block, so there is exactly one
   * place where "did the user opt into photos?" is asked.
   */
  readonly photos: readonly PhotoRecord[]
  /**
   * Every phase, oldest first, with its corrections.
   *
   * Present so a reader can see that a date was **amended** rather than reading
   * a late correction as the original record — the reason D-070 rejected a
   * silent overwrite.
   */
  readonly phases: readonly PhaseRecord[]
}

/** Named here rather than imported from `services/`, which `exports/` sits beside
    rather than below. The service builds it; this is the shape it must produce. */
export interface ExportLiftSummary {
  readonly lift: SentinelLiftRecord
  readonly summary: LiftSummary
}

export interface ReviewWithOpinion {
  readonly review: WeeklyReviewRecord
  /**
   * The week as it was actually executed (D-105 §12).
   *
   * Absent for every week finalized before C7.5, and for any week the user never
   * opened the app in. **Absent means no record**, never an empty week — the
   * serializer says which, and the two mean opposite things.
   */
  readonly trainingWeek?: TrainingWeekRecord
  /** Absent only if a finalized review's opinion could not be read back. */
  readonly opinion: OpinionRecord | undefined
}

/** Age in whole years on the reference date (D-038). */
export function ageInWholeYears(dateOfBirth: CalendarDate, reference: CalendarDate): number {
  const [birthYear, birthMonth, birthDay] = splitDate(dateOfBirth)
  const [year, month, day] = splitDate(reference)

  const hadBirthday = month > birthMonth || (month === birthMonth && day >= birthDay)
  return year - birthYear - (hadBirthday ? 0 : 1)
}

function splitDate(date: CalendarDate): [number, number, number] {
  const [year, month, day] = (date as string).split('-').map(Number)
  return [year ?? 0, month ?? 0, day ?? 0]
}
